import React, { Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Featured
from '@fuww/components/src/Articles/Sections/Featured';
import BigCard from '@fuww/components/src/Cards/Big';
import { SmallCard } from '@fuww/components/src/Cards/Small';
import WideCard from '@fuww/components/src/Cards/Wide';
import breakpoints from '@fuww/components/src/Theme/breakpoints';
import DynamicSideBar from './DynamicSidebar';
import LinkAndAnchor from '../../../LinkAndAnchor';
import SiteContext from '../../../SiteContext';
import getArticlesParameters from '../../../../lib/getArticlesParameters';
import getProxiedImages from '../../../../lib/getProxiedImages';
import ImageWithUrls from '../../../ImageWithUrls';
import FormattedDate from '../../../FormattedDate';
import InView from '../../../InView';
import getCategoryTitle from '../../../../lib/getCategoryTitle';

const FeaturedSection = ({
  featuredArticles, variables, showCreatedAt,
}) => {
  const intl = useIntl();
  const { hasLocalNews } = useContext(SiteContext);

  const [
    firstArticle,
    secondArticle,
    thirdArticle,
    fourthArticle,
  ] = featuredArticles;

  if (!firstArticle) return null;

  const {
    imageUrls: firstArticleImageUrls,
    srcSet: firstArticleSourceSet,
  } = getProxiedImages(
    variables, firstArticle.imageUrls,
  );

  const {
    imageUrls: fourthArticleImageUrls,
    srcSet: fourthArticleSourceSet,
  } = fourthArticle
    ? getProxiedImages(
      variables, fourthArticle.imageUrls,
    ) : {};

  return (
    <>
      <Featured
        spotlightArticle={
          (
            <LinkAndAnchor
              route="article"
              params={getArticlesParameters(firstArticle)}
              href={firstArticle.path}
              useRouter
            >
              <BigCard
                image={(
                  <ImageWithUrls
                    imageUrls={firstArticleImageUrls}
                    alt={firstArticle.title}
                    sizes={`
                      (min-width: 1440px) 621px,
                      (min-width: ${breakpoints.lg}px) 44vw,
                      (min-width: ${breakpoints.md}px) 62vw,
                      (min-width: ${breakpoints.sm}px) 97vw,
                      100vw
                    `}
                    srcSet={firstArticleSourceSet}
                    loadStrategy="instant"
                    finalFetchpriority="high"
                    placeholderFetchpriority="high"
                    preload
                  />
                )}
                category={
                  getCategoryTitle(firstArticle.category, hasLocalNews, intl)
                }
                labels={firstArticle.labels}
                createdAt={showCreatedAt
                  && <FormattedDate value={firstArticle.createdAt} />}
                title={firstArticle.title}
                description={firstArticle.summary}
                withPageMargins
              />
            </LinkAndAnchor>
          )
        }
        sidebarArticles={
          [secondArticle, thirdArticle].map((newsArticle) => {
            if (!newsArticle) return null;

            const {
              category,
              title,
              summary,
              createdAt,
              imageUrls,
              path,
              id,
              labels,
            } = newsArticle;

            const {
              imageUrls: newsArticleImageUrls,
              srcSet: newsArticleSourceSet,
            } = getProxiedImages(variables, imageUrls);

            return (
              <Fragment key={id}>
                <LinkAndAnchor
                  route="article"
                  params={getArticlesParameters(newsArticle)}
                  href={path}
                  useRouter
                >
                  <SmallCard
                    image={(
                      <ImageWithUrls
                        imageUrls={newsArticleImageUrls}
                        alt={title}
                        sizes={`
                          (min-width: 1440px) 335px,
                          (min-width: ${breakpoints.lg}px) 24vw,
                          (min-width: ${breakpoints.md}px) 33vw,
                          (min-width: ${breakpoints.sm}px) 47vw,
                          100vw
                        `}
                        srcSet={newsArticleSourceSet}
                      />
                    )}
                    category={getCategoryTitle(category, hasLocalNews, intl)}
                    labels={labels}
                    createdAt={showCreatedAt
                      && <FormattedDate value={createdAt} />}
                    title={title}
                    description={summary}
                    withPageMargins
                  />
                </LinkAndAnchor>
              </Fragment>
            );
          })
        }
        wideArticle={
          fourthArticle ? (
            <LinkAndAnchor
              route="article"
              params={getArticlesParameters(fourthArticle)}
              href={fourthArticle.path}
              useRouter
            >
              <WideCard
                category={
                  getCategoryTitle(fourthArticle.category, hasLocalNews, intl)
                }
                title={fourthArticle.title}
                createdAt={showCreatedAt
                  && <FormattedDate value={fourthArticle.createdAt} />}
                description={fourthArticle.summary}
                imageUrls={fourthArticle.imageUrls}
                labels={fourthArticle.labels}
                image={(
                  <ImageWithUrls
                    imageUrls={fourthArticleImageUrls}
                    alt={fourthArticle.title}
                    sizes={`
                      (min-width: 1440px) 503px,
                      (min-width: ${breakpoints.lg}px) 35vw,
                      (min-width: ${breakpoints.sm}px) 49vw,
                      100vw
                    `}
                    srcSet={fourthArticleSourceSet}
                  />
                )}
                withPageMargins
              />
            </LinkAndAnchor>
          ) : null
        }
        sideBar={
          (
            <InView>
              <DynamicSideBar />
            </InView>
          )
        }
      />
    </>
  );
};

FeaturedSection.propTypes = {
  featuredArticles: PropTypes.arrayOf(PropTypes.shape({})),
  variables: PropTypes.shape({}).isRequired,
  showCreatedAt: PropTypes.bool,
};

FeaturedSection.defaultProps = {
  featuredArticles: [],
  showCreatedAt: false,
};

export default FeaturedSection;
